import React, { useState, useEffect } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import {
  Image,
  FileText,
  Code,
  MessageSquare,
  Inbox,
  BookOpen,
  Webhook,
  Cpu,
  Globe,
  Zap,
  Database,
  Eye,
  BarChart,
  DollarSign,
  Smile,
  Building2,
  Megaphone,
  GitBranch,
  Users,
  LogOut,
  Clock,
  HelpCircle,
  Sliders,
  Sparkles,
  Plus,
  MoreHorizontal,
  Trash2,
  Copy,
  Layers,
} from "lucide-react";
import api from "../utils/api";

// Chevron Icon for dropdown arrows and collapsible headers
const IconChevron = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="transition-transform duration-200"
    style={{
      transform: open ? "rotate(180deg)" : "rotate(0deg)",
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Sidebar = ({ 
  activeTab, 
  setActiveTab, 
  isCollapsed, 
  setIsCollapsed, 
  activeChatsCount = 0,
  activeProduct = "ai-agents",
  setActiveProduct
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Switcher states & flow list states
  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const [flows, setFlows] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [flowMenuOpenId, setFlowMenuOpenId] = useState(null);
  const [flowActionLoading, setFlowActionLoading] = useState(null);

  // Auto-detect flows product if URL starts with /flow-builder or activeProduct is 'flows'
  const effectiveProduct = (location.pathname.startsWith("/flow-builder") || activeProduct === "flows") ? "flows" : "ai-agents";

  // Get user role from session
  const userStr = sessionStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.role === 'admin';

  const fetchFlows = React.useCallback(async () => {
    setLoadingFlows(true);
    try {
      const data = await api.getBotFlows();
      setFlows(data || []);
    } catch (err) {
      console.error("Failed to fetch flows for sidebar:", err);
    } finally {
      setLoadingFlows(false);
    }
  }, []);

  useEffect(() => {
    if (effectiveProduct === "flows") {
      fetchFlows();
    }
  }, [effectiveProduct, fetchFlows]);

  const handleDeleteFlow = async (e, flowId) => {
    e.preventDefault();
    e.stopPropagation();
    setFlowMenuOpenId(null);
    setFlowActionLoading(flowId);
    try {
      await api.deleteBotFlow(flowId);
      await fetchFlows();
      // Navigate away if we deleted the current flow
      if (location.search.includes(`id=${flowId}`)) {
        navigate("/flow-builder");
      }
    } catch (err) {
      console.error("Failed to delete flow:", err);
    } finally {
      setFlowActionLoading(null);
    }
  };

  const handleDuplicateFlow = async (e, flow) => {
    e.preventDefault();
    e.stopPropagation();
    setFlowMenuOpenId(null);
    setFlowActionLoading(flow.id);
    try {
      // Fetch full details to get content
      const detail = await api.getBotFlowDetail(flow.id);
      await api.saveBotFlow({
        name: `${detail.name} (Copy)`,
        description: detail.description || '',
        content: detail.content || '{}',
        keywords: detail.keywords || [],
        is_active: true,
      });
      await fetchFlows();
    } catch (err) {
      console.error("Failed to duplicate flow:", err);
    } finally {
      setFlowActionLoading(null);
    }
  };

  // Grouped sections matching the user's screenshot layout and dropdown nested behavior
  const sections = [
    {
      title: "", // Top uncategorized section
      items: [
        { id: "human-agent-chat", label: "Live Chat", icon: MessageSquare, path: "/human-agent-chat", type: "single", hasBadge: false }
      ]
    },
    {
      title: "BOT BUILDER",
      items: [
        { id: "instructions", label: "AI Agent Instructions", icon: Sliders, path: "/instructions", type: "single" },
        { id: "skills", label: "Skills", icon: Sparkles, path: "/skills", type: "single" },
        { id: "business-flows", label: "Business Flows", icon: GitBranch, path: "/business-flows", type: "single" },
        { id: "api-tools", label: "API Tools", icon: Code, path: "/api-tools", type: "single" },
        {
          id: "sources",
          label: "Sources",
          icon: Database,
          type: "dropdown",
          subItems: [
            { id: "documents", label: "Knowledge Base", path: "/documents", icon: FileText },
            { id: "knowledge-graph", label: "Knowledge Graph", path: "/knowledge-graph", icon: Database },
            { id: "smart-faq", label: "Smart FAQ Builder", path: "/smart-faq", icon: BookOpen }
          ]
        }
      ]
    },
    {
      title: "COMMUNICATION",
      items: [
        { id: "chat", label: "Copilot", icon: Inbox, path: "/chat", type: "single" },
        { id: "campaigns", label: "Campaigns", icon: Megaphone, path: "/campaigns", type: "single" },
        { id: "contacts", label: "Contacts", icon: Users, path: "/contacts", type: "single" },
        { id: "segments", label: "Segments", icon: Layers, path: "/segments", type: "single" },
        { id: "reports", label: "Reports", icon: BarChart, path: "/reports", type: "single" }
      ]
    },
    {
      title: "ANALYTICS",
      items: [
        { id: "analytics-overview", label: "Overview", path: "/analytics?tab=overview", icon: BarChart, type: "single" },
        { id: "analytics-tokens", label: "Tokens", path: "/analytics?tab=token-analytics", icon: Cpu, type: "single" },
        { id: "analytics-cost", label: "Cost Breakdown", path: "/analytics?tab=cost-analytics", icon: DollarSign, type: "single" },
        { id: "analytics-sessions", label: "Sessions", path: "/analytics?tab=sessions", icon: Clock, type: "single" },
        { id: "analytics-conversations", label: "Conversations", path: "/analytics?tab=conversations", icon: MessageSquare, type: "single" },
        { id: "analytics-sentiment", label: "Sentiment", path: "/analytics?tab=sentiment", icon: Smile, type: "single" },
        { id: "analytics-evaluations", label: "AI Evaluations", path: "/evaluations", icon: Zap, type: "single" }
      ]
    },
    {
      title: "SETTINGS",
      items: [
        {
          id: "workspace",
          label: "Workspace",
          icon: Building2,
          type: "dropdown",
          subItems: [
            { id: "ai-agents", label: "AI Agents", path: "/ai-agents", icon: Building2 },
            { id: "human-agents", label: "Human Agents", path: "/human-agents", icon: Users },
            { id: "human-agent-groups", label: "Human Agent Groups", path: "/human-agent-groups", icon: Building2 },
            { id: "logo", label: "Logo Management", path: "/logo", icon: Image },
            { id: "domains", label: "Domain Management", path: "/domains", icon: Globe },
            { id: "widget", label: "Widget Integration", path: "/widget", icon: Code },
            { id: "widget-preview", label: "Widget Preview", path: "/widget-preview", icon: Eye }
          ]
        },
        { id: "user-management", label: "Teams & Roles", icon: Users, path: "/user-management", type: "single" },
        { id: "billing", label: "Billing & Plans", icon: DollarSign, path: "/billing", type: "single" },
        { id: "integrations", label: "Integrations", icon: Zap, path: "/integrations", type: "single" },
        { id: "models", label: "Models", icon: Cpu, path: "/models", type: "single" },
        { id: "webhooks", label: "Webhooks", icon: Webhook, path: "/webhooks", type: "single" }
      ]
    },
    {
      title: "SUPPORT",
      items: [
        { id: "docs", label: "Help Center", icon: HelpCircle, path: "/docs", type: "single" }
      ]
    }
  ];

  // RBAC filter: Agents only see the Live Chat route
  const filteredSections = sections.map(section => {
    return {
      ...section,
      items: section.items.filter(item => {
        if (!isAdmin) {
          return item.id === "human-agent-chat";
        }
        return true;
      })
    };
  }).filter(section => section.items.length > 0);

  // Initialize Collapsible Section states (with auto-expansion of current active section)
  const [expandedSections, setExpandedSections] = useState(() => {
    const initial = {
      "BOT BUILDER": true,
      "COMMUNICATION": true,
      "ANALYTICS": true,
      "SETTINGS": true,
      "SUPPORT": true
    };
    
    sections.forEach(sec => {
      if (sec.title) {
        const hasActiveItem = sec.items.some(item => {
          if (item.type === "single") {
            return location.pathname === item.path || (item.path && item.path.includes("?") && location.pathname + location.search === item.path);
          }
          if (item.type === "dropdown") {
            return item.subItems.some(sub => location.pathname === sub.path);
          }
          return false;
        });
        if (hasActiveItem) {
          initial[sec.title] = true;
        }
      }
    });
    
    return initial;
  });

  const toggleSection = (title) => {
    if (!title) return;
    setExpandedSections(prev => ({
      ...prev,
      [title]: !prev[title]
    }));
  };

  // Dropdown States for nested menus inside Bot Builder and Settings
  const isSourcesActive = filteredSections.find(s => s.title === "BOT BUILDER")?.items
    .find(i => i.id === "sources")?.subItems?.some(sub => location.pathname === sub.path);
  const [sourcesOpen, setSourcesOpen] = useState(!!isSourcesActive);

  const isWorkspaceActive = filteredSections.find(s => s.title === "SETTINGS")?.items
    .find(i => i.id === "workspace")?.subItems?.some(sub => location.pathname === sub.path);
  const [workspaceOpen, setWorkspaceOpen] = useState(!!isWorkspaceActive);

  const toggleDropdown = (id) => {
    if (id === "sources") setSourcesOpen(!sourcesOpen);
    if (id === "workspace") setWorkspaceOpen(!workspaceOpen);
  };

  const isDropdownOpen = (id) => {
    if (id === "sources") return sourcesOpen;
    if (id === "workspace") return workspaceOpen;
    return false;
  };

  // Helper check for active sub-items with exact search/query matching
  const isPathActive = (path) => {
    if (!path) return false;
    return location.pathname + location.search === path || 
      (path === "/analytics?tab=overview" && location.pathname === "/analytics" && !location.search);
  };

  const handleProductSwitch = (product) => {
    if (setActiveProduct) {
      setActiveProduct(product);
    } else {
      localStorage.setItem("activeProduct", product);
    }
    setIsSwitcherOpen(false);
    if (product === "flows") {
      navigate("/flow-builder");
    } else {
      navigate("/admin");
    }
  };

  const handleCreateNewFlow = () => {
    // Implement navigation or modal logic for creating a new flow
    navigate("/flow-builder/new");
  };

  return (
    <>
      {/* Mobile backdrop when sidebar is open on small screens */}
      {!isCollapsed && (
        <div
          onClick={() => setIsCollapsed(true)}
          className="fixed inset-0 bg-black/40 backdrop-blur-xs z-[190] md:hidden transition-opacity duration-300"
          aria-hidden="true"
        />
      )}

      <aside
        className={`${
          isCollapsed ? "-translate-x-full md:translate-x-0 md:w-20" : "translate-x-0 w-64 md:w-56"
        } fixed md:static inset-y-0 left-0 z-[200] md:z-auto flex flex-col bg-white border-r border-gray-200 shrink-0 transition-all duration-300 shadow-xl md:shadow-sm`}
      >
        {/* Original Image Logo Header */}
        <div className={`h-[72px] border-b border-gray-100 flex items-center shrink-0 px-4 ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        {!isCollapsed && (
          <div className="flex items-center transition-all duration-300 w-48">
            <NavLink to="/admin" className="cursor-pointer">
              <img
                src="/logo.png"
                alt="EasyChat"
                className="h-18 w-auto object-contain transition-all duration-300"
              />
            </NavLink>
          </div>
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-700">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      </div>

      {/* Premium Workspace Switcher Dropdown (ElevenLabs Style) */}
      <div className="px-3 py-3 border-b border-gray-100 relative">
        <button
          onClick={() => setIsSwitcherOpen(!isSwitcherOpen)}
          className={`w-full flex items-center justify-between bg-gray-50/70 border border-gray-200/80 hover:bg-gray-50 hover:border-gray-300 rounded-2xl px-3.5 py-2.5 text-left transition-all ${
            isCollapsed ? "justify-center" : ""
          }`}
          title={effectiveProduct === "ai-agents" ? "Easy AI Agents Workspace" : "EasyFlows Workspace"}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            {effectiveProduct === "ai-agents" ? (
              <div className="w-5 h-5 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                <Users size={12} />
              </div>
            ) : (
              <div className="w-5 h-5 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0">
                <GitBranch size={12} />
              </div>
            )}
            {!isCollapsed && (
              <div className="flex flex-col min-w-0">
                <span className="text-xs font-black text-gray-800 tracking-wide uppercase leading-none">
                  {effectiveProduct === "ai-agents" ? "Easy AI Agents" : "EasyFlows"}
                </span>
              </div>
            )}
          </div>
          {!isCollapsed && (
            <span className="text-gray-400 shrink-0 ml-2">
              <IconChevron open={isSwitcherOpen} />
            </span>
          )}
        </button>

        {isSwitcherOpen && (
          <div className={`absolute ${isCollapsed ? "left-20 w-48" : "left-3 right-3"} top-[54px] mt-1 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden py-1.5 animate-slideIn`}>
            <button
              onClick={() => handleProductSwitch("ai-agents")}
              className={`w-full flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors ${
                effectiveProduct === "ai-agents" ? "bg-blue-50/40" : ""
              }`}
            >
              <div className="w-5 h-5 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 mt-0.5">
                <Users size={12} />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-black text-gray-800 tracking-wide uppercase">Easy AI Agents</span>
                <span className="text-[9px] text-gray-400 font-medium leading-tight mt-0.5">Deploy & monitor conversational AI Agents</span>
              </div>
            </button>
            <button
              onClick={() => handleProductSwitch("flows")}
              className={`w-full flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50 text-left border-t border-gray-100 transition-colors ${
                effectiveProduct === "flows" ? "bg-emerald-50/40" : ""
              }`}
            >
              <div className="w-5 h-5 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0 mt-0.5">
                <GitBranch size={12} />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-black text-gray-800 tracking-wide uppercase">EasyFlows</span>
                <span className="text-[9px] text-gray-400 font-medium leading-tight mt-0.5">Orchestrate visual chatbot workflows</span>
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Navigation list */}
      <nav className="flex-1 px-2.5 py-3 overflow-y-auto overflow-x-hidden space-y-4 custom-scrollbar">
        {effectiveProduct === "flows" ? (
          <div className="space-y-4">
            {isCollapsed ? (
              <div className="flex justify-center px-1 py-2">
                <button
                  onClick={handleCreateNewFlow}
                  className="w-10 h-10 flex items-center justify-center text-[rgb(17,24,38)] hover:text-black transition-all active:scale-95 bg-transparent"
                  title="New Flow"
                >
                  <Plus size={22} strokeWidth={3} />
                </button>
              </div>
            ) : (
              <div className="px-2">
                <button
                  onClick={handleCreateNewFlow}
                  className="w-full flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white py-2.5 rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-md active:scale-95"
                >
                  <Plus size={14} />
                  New Flow
                </button>
              </div>
            )}

            <div className="space-y-1">
              {!isCollapsed && (
                <div className="px-4 text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center justify-between">
                  <span>Visual Flows</span>
                  <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded text-[8px] font-bold">
                    {flows.length}
                  </span>
                </div>
              )}
              {loadingFlows ? (
                <div className="flex flex-col items-center justify-center py-6 gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-900 border-t-transparent"></div>
                </div>
              ) : flows.length > 0 ? (
                flows.map((flow) => {
                  const isActiveFlow = location.search.includes(`id=${flow.id}`) || (location.pathname === `/flow-builder/${flow.id}`);
                  const isMenuOpen = flowMenuOpenId === flow.id;
                  const isLoading = flowActionLoading === flow.id;
                  return (
                    <div key={flow.id} className="relative group">
                      <NavLink
                        to={`/flow-builder?id=${flow.id}`}
                        onClick={() => setActiveTab(`flow-${flow.id}`)}
                        className={`flex items-center min-h-[34px] rounded-[10px] text-xs font-semibold px-4 transition-all duration-200 mb-0.5 pr-8 ${
                          isActiveFlow
                            ? "bg-[#83bbe1] text-white shadow-md shadow-blue-600/10"
                            : "text-gray-600 hover:text-gray-900 hover:bg-blue-50/50"
                        }`}
                        title={flow.name}
                      >
                        <div className="flex items-center w-full min-w-0">
                          <div className="w-5 h-5 flex items-center justify-center shrink-0">
                            {isLoading ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent" />
                            ) : (
                              <GitBranch
                                size={15}
                                className={isActiveFlow ? "text-white" : "text-gray-500"}
                              />
                            )}
                          </div>
                          <div className={`flex-1 overflow-hidden transition-all duration-300 flex items-center justify-between ${
                            isCollapsed ? "w-0 opacity-0 ml-0 pointer-events-none" : "w-full opacity-100 ml-3"
                          }`}>
                            <span className="truncate block font-bold capitalize">{flow.name}</span>
                            {flow.is_active && (
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse ml-2 shrink-0"></span>
                            )}
                          </div>
                        </div>
                      </NavLink>

                      {/* 3-dot menu button */}
                      {!isCollapsed && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setFlowMenuOpenId(isMenuOpen ? null : flow.id);
                          }}
                          className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center rounded-md transition-all z-10 ${
                            isActiveFlow
                              ? 'text-white/80 hover:text-white hover:bg-white/20'
                              : 'text-gray-400 hover:text-gray-700 hover:bg-gray-200 opacity-0 group-hover:opacity-100'
                          } ${isMenuOpen ? 'opacity-100 !text-gray-700 !bg-gray-200' : ''}`}
                          title="Flow options"
                        >
                          <MoreHorizontal size={13} />
                        </button>
                      )}

                      {/* Dropdown menu */}
                      {isMenuOpen && !isCollapsed && (
                        <>
                          {/* Backdrop to close */}
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setFlowMenuOpenId(null)}
                          />
                          <div className="absolute left-2 right-2 z-50 mt-0.5 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-slideIn"
                            style={{ top: '100%' }}
                          >
                            <button
                              onClick={(e) => handleDuplicateFlow(e, flow)}
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors text-left"
                            >
                              <Copy size={13} className="shrink-0" />
                              Duplicate
                            </button>
                            <button
                              onClick={(e) => handleDeleteFlow(e, flow.id)}
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 transition-colors text-left border-t border-gray-100"
                            >
                              <Trash2 size={13} className="shrink-0" />
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
              ) : (
                !isCollapsed && (
                  <div className="px-4 py-3 border border-dashed border-gray-200 rounded-2xl text-[10px] font-bold text-gray-400 text-center">
                    No sequences found
                  </div>
                )
              )}
            </div>
          </div>
        ) : (
          filteredSections.map((section, secIdx) => {
            const isSectionExpanded = !section.title || expandedSections[section.title];

            return (
              <div key={secIdx} className="space-y-1">
                {/* Collapsible Section Title */}
                {section.title && (
                  isCollapsed ? (
                    <div className="w-full flex items-center justify-center h-6 mb-0.5" key={section.title + "-divider"}>
                      <div className="w-8 border-t border-gray-200" />
                    </div>
                  ) : (
                    <button
                      onClick={() => toggleSection(section.title)}
                      className="w-full flex items-center justify-between text-[9px] font-bold text-gray-400 uppercase tracking-widest px-4 h-6 rounded-lg hover:bg-gray-50/50 hover:text-gray-900 transition-all duration-200 mb-0.5"
                    >
                      <span>{section.title}</span>
                      <span className="text-gray-400">
                        <IconChevron open={isSectionExpanded} />
                      </span>
                    </button>
                  )
                )}

                {/* Collapsible Section Items Container */}
                <div
                  style={{
                    maxHeight: isSectionExpanded ? "2000px" : "0px",
                    overflow: "hidden",
                    transition: "max-height 0.25s ease-in-out",
                  }}
                  className="space-y-0.5"
                >
                  {section.items.map((item) => {
                    const { id, label, path, icon: Icon, type, hasBadge, subItems } = item;

                    if (type === "single") {
                      return (
                        <NavLink
                          key={id}
                          to={path}
                          onClick={() => {
                            setActiveTab(id);
                            if ((id === "contacts" || id === "human-agent-chat") && setIsCollapsed) {
                              setIsCollapsed(true);
                            }
                          }}
                          className={({ isActive }) => {
                            const effectiveActive = (path && path.includes("?")) ? isPathActive(path) : isActive;
                            return `group relative flex items-center min-h-[34px] rounded-[10px] text-xs font-semibold transition-all duration-200 mb-0.5 px-4 ${effectiveActive
                              ? "bg-[#83bbe1] text-white shadow-md shadow-blue-600/10"
                              : "text-gray-600 hover:text-gray-900 hover:bg-blue-50/50"
                              }`;
                          }}
                        >
                          {({ isActive }) => {
                            const effectiveActive = (path && path.includes("?")) ? isPathActive(path) : isActive;
                            return (
                              <div className="flex items-center w-full min-w-0">
                                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                  <Icon
                                    size={15}
                                    className={`transition-colors ${effectiveActive ? "text-white" : "text-gray-500 group-hover:text-gray-700"}`}
                                  />
                                </div>
                                <div className={`flex-1 flex items-center justify-between overflow-hidden transition-all duration-300 ${
                                  isCollapsed ? "w-0 opacity-0 ml-0 pointer-events-none" : "w-full opacity-100 ml-3"
                                }`}>
                                  <span className="truncate">{label}</span>
                                  {hasBadge && activeChatsCount > 0 && (
                                    <span className={`flex items-center justify-center px-1 py-0.5 text-[9px] font-bold rounded-full min-w-[16px] ml-2 ${effectiveActive ? "bg-white text-[#83bbe1]" : "bg-[#83bbe1] text-white"}`}>
                                      {activeChatsCount}
                                    </span>
                                  )}
                                </div>
                                {/* Live count notification badge in collapsed state */}
                                {isCollapsed && hasBadge && activeChatsCount > 0 && (
                                  <div className="absolute right-3.5 w-1.5 h-1.5 bg-[#83bbe1] rounded-full" />
                                )}
                              </div>
                            );
                          }}
                        </NavLink>
                      );
                    }

                    if (type === "dropdown") {
                      const isOpen = isDropdownOpen(id);
                      const dropdownActive = subItems.some(sub => location.pathname === sub.path);

                      return (
                        <React.Fragment key={id}>
                          <button
                            onClick={() => {
                              if (isCollapsed) {
                                if (setIsCollapsed) {
                                  setIsCollapsed(false);
                                }
                                if (id === "sources") {
                                  setSourcesOpen(true);
                                  setExpandedSections(prev => ({ ...prev, "BOT BUILDER": true }));
                                }
                                if (id === "workspace") {
                                  setWorkspaceOpen(true);
                                  setExpandedSections(prev => ({ ...prev, "SETTINGS": true }));
                                }
                              } else {
                                toggleDropdown(id);
                              }
                            }}
                            className={`group relative w-full flex items-center min-h-[34px] rounded-[10px] text-xs font-semibold transition-all duration-200 mb-0.5 px-4 ${dropdownActive
                              ? "bg-[#83bbe1] text-white shadow-md shadow-blue-600/10"
                              : "text-gray-600 hover:text-gray-900 hover:bg-blue-50/50"
                              }`}
                          >
                            <div className="flex items-center w-full min-w-0">
                              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                <Icon
                                  size={15}
                                  className={`transition-colors ${dropdownActive ? "text-white" : "text-gray-500 group-hover:text-gray-700"}`}
                                />
                              </div>
                              <div className={`flex-1 flex items-center justify-between overflow-hidden transition-all duration-300 ${
                                isCollapsed ? "w-0 opacity-0 ml-0 pointer-events-none" : "w-full opacity-100 ml-3"
                              }`}>
                                <span className="truncate">{label}</span>
                                <span className={`${dropdownActive ? "text-white" : "text-gray-400 group-hover:text-gray-600"} ml-2 shrink-0`}>
                                  <IconChevron open={isOpen} />
                                </span>
                              </div>
                            </div>
                          </button>

                          {/* Submenu links */}
                          {!isCollapsed && isOpen && (
                            <div className="space-y-0.5 mt-0.5">
                              {subItems.map((subItem) => {
                                const subActive = isPathActive(subItem.path);
                                return (
                                  <NavLink
                                    key={subItem.id}
                                    to={subItem.path}
                                    onClick={() => setActiveTab(subItem.id)}
                                    className={({ isActive }) => {
                                      const effectiveActive = isActive || subActive;
                                      return `group relative flex items-center min-h-[34px] rounded-[10px] text-xs font-semibold transition-all duration-200 mb-0.5 px-4 ${effectiveActive
                                        ? "bg-[#83bbe1] text-white shadow-md shadow-blue-600/10"
                                        : "text-gray-600 hover:text-gray-900 hover:bg-blue-50/50"
                                        }`;
                                    }}
                                  >
                                    {({ isActive }) => {
                                      const effectiveActive = isActive || subActive;
                                      return (
                                        <div className="flex items-center w-full min-w-0">
                                          <div className="w-5 h-5 flex items-center justify-center shrink-0">
                                            <subItem.icon
                                              size={15}
                                              className={`transition-colors ${effectiveActive ? "text-white" : "text-gray-500 group-hover:text-gray-700"}`}
                                            />
                                          </div>
                                          <div className="flex-1 flex items-center justify-between overflow-hidden ml-3">
                                            <span className="truncate">{subItem.label}</span>
                                          </div>
                                        </div>
                                      );
                                    }}
                                  </NavLink>
                                );
                              })}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    }

                    return null;
                  })}
                </div>
              </div>
            );
          })
        )}
      </nav>

      {/* Reverted Footer: Original Sign Out Button */}
      <div className="border-t border-gray-100 px-2.5 py-2">
        <button
          onClick={() => {
            localStorage.removeItem("user");
            localStorage.removeItem("activeAiAgentId");
            localStorage.removeItem("activeTenantId");
            localStorage.removeItem("activeProduct");
            sessionStorage.removeItem("user");
            sessionStorage.clear();
            window.location.href = "/login";
          }}
          className={`w-full flex items-center min-h-[34px] rounded-[10px] text-xs font-semibold text-gray-600 hover:text-white hover:bg-[#83bbe1] transition-colors duration-150 group px-4`}
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <LogOut
              size={14}
              className="text-gray-900 group-hover:text-white transition-colors"
            />
          </div>
          <div className={`flex items-center whitespace-nowrap overflow-hidden transition-all duration-300 ${isCollapsed ? "w-0 opacity-0" : "w-full opacity-100 ml-3"}`}>
            Sign Out
          </div>
        </button>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
